import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
// 🟢 NEW: Import Auth Context
import { useAuth } from "./AuthContext";

const CRMContext = createContext();
export const useCRM = () => useContext(CRMContext);

// Helper Map for Optimistic Updates
const STAGE_MAP = {
  'New Inquiry': 'new_inquiry',
  'new_inquiry': 'new_inquiry',
  'Site Visit / Estimation': 'estimation',
  'Site Visit': 'estimation',
  'Estimation': 'estimation',
  'estimation': 'estimation',
  'Proposal Sent': 'proposal_sent',
  'proposal_sent': 'proposal_sent',
  'Negotiation': 'negotiation',
  'negotiation': 'negotiation',
  'Won / Contract': 'won',
  'Won': 'won',
  'won': 'won',
  'Booked': 'won',
  'Sold': 'won',
  'Lost': 'lost',
  'lost': 'lost'
};

export const CRMProvider = ({ children }) => {
  const [columns, setColumns] = useState({});
  const [isQuickAddVisible, setIsQuickAddVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [movingLeadIds, setMovingLeadIds] = useState({});
  const moveAbortControllers = useRef({});
  
  // 🟢 NEW: Get the current user
  const { user } = useAuth();

  // 1. FETCH PIPELINE
  const fetchPipeline = useCallback(async () => {
    try {
      const res = await api.get("/crm/pipeline");
      setColumns(res.data);
    } catch (err) {
      console.error("Error fetching CRM:", err);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.id) {
      setColumns({});
    }
  }, [user]);
// 🟢 NEW: Deep-Link single fetch function
  const fetchLeadById = async (id) => {
    try {
      const res = await api.get(`/crm/leads/${id}`);
      return res.data;
    } catch (err) {
      console.error("Failed to fetch lead", err);
      return null;
    }
  };
  // 2. 🟢 UPDATE LEAD (The Fix)
  const updateLead = async (leadId, updatedFields) => {
    // A. Optimistic Update (Update UI immediately)
    setColumns(prevColumns => {
      let newColumns = JSON.parse(JSON.stringify(prevColumns)); // Deep copy
      let foundLead = null;
      let oldColId = null;

      // Find the lead
      for (const colId in newColumns) {
        const index = newColumns[colId].items.findIndex(item => String(item.id) === String(leadId));
        if (index !== -1) {
          foundLead = { ...newColumns[colId].items[index], ...updatedFields };
          oldColId = colId;
          
          // Remove from old column
          newColumns[colId].items.splice(index, 1);
          break;
        }
      }

      if (!foundLead) return prevColumns;

      // Determine New Column ID (if stage changed)
      let newColId = oldColId;
      if (updatedFields.stage && STAGE_MAP[updatedFields.stage]) {
        newColId = STAGE_MAP[updatedFields.stage];
      } else if (updatedFields.stageId && newColumns[updatedFields.stageId]) {
        newColId = updatedFields.stageId;
      }

      // Add to column
      if (newColumns[newColId]) {
          newColumns[newColId].items.unshift(foundLead);
      } else {
          // Fallback if column doesn't exist
          newColumns[oldColId].items.unshift(foundLead);
      }

      return newColumns;
    });

    // B. API Call
    try {
        await api.put(`/crm/leads/${leadId}`, updatedFields);
        // Silent success (Auto-save doesn't need toast)
    } catch (err) {
        console.error(err);
        toast.error("Failed to save changes");
    }
  };

  const getLeadActivities = async (leadId) => {
    try {
        const res = await api.get(`/crm/leads/${leadId}/activities`);
        return res.data;
    } catch (err) {
        console.error("Failed to fetch activities");
        return [];
    }
  };

  // 3. MOVE CARD (Drag & Drop with Graceful In-Flight State & Instant Rollback)
  const moveCard = useCallback(async (sourceColId, destColId, sourceIndex, destIndex) => {
    if (sourceColId === destColId && sourceIndex === destIndex) {
      return;
    }

    let movedLead = null;
    let rollbackColumns = null;

    setColumns(prevColumns => {
      if (!prevColumns || !prevColumns[sourceColId] || !prevColumns[destColId]) return prevColumns;
      rollbackColumns = prevColumns;

      const newColumns = { ...prevColumns };
      const sourceItems = [...(newColumns[sourceColId]?.items || [])];
      const destItems = sourceColId === destColId ? sourceItems : [...(newColumns[destColId]?.items || [])];

      const [item] = sourceItems.splice(sourceIndex, 1);
      if (!item) return prevColumns;

      const updatedItem = {
        ...item,
        stageId: destColId,
        stage: (newColumns[destColId] && newColumns[destColId].title) || destColId
      };

      destItems.splice(destIndex, 0, updatedItem);
      movedLead = updatedItem;

      newColumns[sourceColId] = {
        ...newColumns[sourceColId],
        items: sourceItems
      };

      if (sourceColId !== destColId) {
        newColumns[destColId] = {
          ...newColumns[destColId],
          items: destItems
        };
      }

      return newColumns;
    });

    if (!movedLead) return;

    // Moving within the exact same column: stage didn't change, no API request needed
    if (sourceColId === destColId) {
      return;
    }

    const leadIdStr = String(movedLead.id);

    // Cancel any previous in-flight move request for this same lead
    if (moveAbortControllers.current[leadIdStr]) {
      try {
        moveAbortControllers.current[leadIdStr].abort();
      } catch (e) {
        // ignore
      }
    }

    const controller = new AbortController();
    moveAbortControllers.current[leadIdStr] = controller;

    // Set moving state so UI displays a sleek non-blocking sync status
    setMovingLeadIds(prev => ({ ...prev, [leadIdStr]: true }));

    try {
      await api.put(
        `/crm/leads/${movedLead.id}/move`,
        { newStageSlug: destColId },
        { signal: controller.signal }
      );

      setMovingLeadIds(prev => {
        const next = { ...prev };
        delete next[leadIdStr];
        return next;
      });
      delete moveAbortControllers.current[leadIdStr];
    } catch (err) {
      // If request was canceled by a subsequent move, don't rollback
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }

      console.error("Failed to move card:", err);
      toast.error("Failed to move card. Reverting...");

      if (rollbackColumns) {
        setColumns(rollbackColumns);
      } else {
        fetchPipeline();
      }

      setMovingLeadIds(prev => {
        const next = { ...prev };
        delete next[leadIdStr];
        return next;
      });
      delete moveAbortControllers.current[leadIdStr];
    }
  }, [fetchPipeline]);

  // 4. ADD NEW LEAD
  const addNewLead = async (leadData) => {
    try {
        const res = await api.post("/crm/leads", leadData);
        
        const newLead = { 
            id: String(res.data.id),
            title: leadData.opportunityName,
            client: leadData.company,
            budget: Number(leadData.expectedRevenue),
            priority: leadData.priority,
            stageId: 'new_inquiry',
            stage: 'New Inquiry',
            unit: leadData.interestedUnit,
            configuration: leadData.configuration
        };

        setColumns(prev => ({
            ...prev,
            new_inquiry: {
                ...prev.new_inquiry,
                items: [newLead, ...prev.new_inquiry.items]
            }
        }));
        
        setIsQuickAddVisible(false);
        toast.success("Opportunity Added");
    } catch (err) {
        toast.error("Failed to add lead");
    }
  };

  const addLeadActivity = async (leadId, activityData) => {
    try {
        await api.post(`/crm/leads/${leadId}/activities`, activityData);
        toast.success("Note Added");
        return true;
    } catch (err) {
        toast.error("Failed to add note");
        return false;
    }
  };

  // 5. DELETE LEAD
  const deleteLead = async (id) => {
    try {
        await api.delete(`/crm/leads/${id}`);
        const newColumns = { ...columns };
        for (const key in newColumns) {
            newColumns[key].items = newColumns[key].items.filter(i => String(i.id) !== String(id));
        }
        setColumns(newColumns);
    } catch (err) {
        toast.error("Failed to delete");
    }
  };

  // 6. Bulk Leads (Placeholder)
  const addBulkLeads = (count) => {
      toast.info("Bulk lead generation feature coming soon!");
  };

  const getColumnTotal = (columnId) => {
    if (!columns[columnId]) return 0;
    return columns[columnId].items.reduce((sum, item) => sum + (item.budget || 0), 0);
  };

  return (
    <CRMContext.Provider value={{ 
        columns, 
        movingLeadIds,
        moveCard, 
        getColumnTotal, 
        addNewLead,
        deleteLead,
        updateLead, // 🟢 NOW EXPOSED
        getLeadActivities,
        addBulkLeads,
        isQuickAddVisible, 
        setIsQuickAddVisible,
        selectedLead, 
        addLeadActivity,
        setSelectedLead,
        fetchLeadById,
        fetchPipeline
    }}>
      {children}
    </CRMContext.Provider>
  );
};